import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AnimatePresence, easeInOut, motion } from 'framer-motion';
import type { Transition } from 'framer-motion';
import { RfqConversationProvider } from 'src/features/rfq-builder/context/RfqConversationContext';
import useRfq from 'src/features/rfq-builder/hooks/useRfq';
import useUpdateRfqStep from 'src/features/rfq-builder/hooks/useUpdateRfqStep';
import RfqBuilderHeader from 'src/features/rfq-builder/ui/RfqBuilderHeader';
import RfqBuilderIntro from 'src/features/rfq-builder/ui/RfqBuilderIntro';
import RfqBuilderLanding from 'src/features/rfq-builder/ui/RfqBuilderLanding';
import RfqPathSelection from 'src/features/rfq-builder/ui/RfqPathSelection';
import RfqPlayground from 'src/features/rfq-builder/ui/RfqPlayground';
import { analyticsService } from 'src/services/analytics/analyticsService';
import {
  RFQ_BUILDER_PAGE_VIEWED,
  RFQ_BUILDER_STEP_VIEWED,
  RFQ_GENERATION_COMPLETED,
  RFQ_GENERATION_ERROR,
  RFQ_GENERATION_STARTED
} from 'src/services/analytics/rfqBuilderEvents';
import createSSEConnection from 'src/shared/config/sseApiService';
import { useMediaQuery, usePageTitle } from 'src/shared/hooks';
import { BuilderStep } from 'src/shared/types/rfq';
import { SSE } from 'sse.js';

import { Spinner } from 'src/shared/components/ui';

const desktopVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0
  })
};

const mobileVariants = {
  enter: (direction: number) => ({
    y: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    y: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    y: direction > 0 ? '-100%' : '100%',
    opacity: 0
  })
};

const desktopTransition: Transition = {
  x: { ease: easeInOut, duration: 1.2 },
  opacity: { ease: easeInOut, duration: 1.2 }
};

const mobileTransition: Transition = {
  y: { ease: easeInOut, duration: 1.2 },
  opacity: { ease: easeInOut, duration: 1.2 }
};

const BuildAnythingPage = () => {
  const [searchParams] = useSearchParams();
  const urlStep = searchParams.get('step');
  const rfqId = searchParams.get('rfq');

  const { data } = useRfq(rfqId as string);

  const pageTitle = data?.rfq?.document?.name
    ? `${data.rfq.document.name} | Cavela`
    : 'New Product | Cavela';

  usePageTitle(pageTitle);
  const [direction, setDirection] = useState(0);
  const isMobile = useMediaQuery();

  // Track if we started with an rfqId (loading existing RFQ vs creating new one)
  const initialRfqIdRef = useRef(rfqId);
  const { mutate: updateStep } = useUpdateRfqStep();

  // Derive effective step:
  // - If URL has a step, use it
  // - If we have an RFQ loaded, use its builder_step (or default to 'playground' for legacy RFQs with null)
  // - Otherwise default to 'landing' (new RFQ flow)
  const step: BuilderStep = useMemo(() => {
    if (urlStep) {
      return urlStep as BuilderStep;
    }
    if (data?.rfq) {
      return (data.rfq.builder_step as BuilderStep) || 'playground';
    }

    return 'landing';
  }, [urlStep, data?.rfq]);

  // Track page view once on mount
  useEffect(() => {
    analyticsService.trackEvent(RFQ_BUILDER_PAGE_VIEWED);
  }, []);

  // Track each step transition
  useEffect(() => {
    analyticsService.trackEvent(RFQ_BUILDER_STEP_VIEWED, {
      step,
      rfq_id: rfqId ?? undefined
    });
  }, [step, rfqId]);

  // RFQ generation state and logic
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const sseRef = useRef<SSE | null>(null);

  const generateRfq = async (id?: number) => {
    const rfqId = id || data?.rfq?.id;

    if (!rfqId) return;

    // Clean up any existing SSE connection
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    setGeneratedContent('');
    setIsGenerating(true);
    analyticsService.trackEvent(RFQ_GENERATION_STARTED, { rfq_id: rfqId });

    const source = createSSEConnection({
      endpoint: `/rfq/${rfqId}/generate/`,
      onMessage: (messageData) => {
        setGeneratedContent((prev) => prev + messageData);
      },
      onSuccess: () => {
        // Close the connection on successful completion
        if (sseRef.current) {
          sseRef.current.close();
          sseRef.current = null;
        }
      },
      onError: (event) => {
        console.error('RFQ generate SSE error', event);
        analyticsService.trackEvent(RFQ_GENERATION_ERROR, { rfq_id: rfqId });
        setGeneratedContent(
          'Sorry, I encountered an error generating the RFQ document.'
        );
        setIsGenerating(false);
      },
      onClose: () => {
        analyticsService.trackEvent(RFQ_GENERATION_COMPLETED, {
          rfq_id: rfqId
        });
        setIsGenerating(false);
      }
    });

    sseRef.current = source;
    source.stream();
  };

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, []);

  // Hide Intercom on mobile for this page
  useEffect(() => {
    const updateIntercomVisibility = () => {
      const intercomElement = document.querySelector(
        '.intercom-lightweight-app'
      ) as HTMLElement;

      if (intercomElement) {
        intercomElement.style.display = isMobile ? 'none' : '';

        return true;
      }

      return false;
    };

    // Try immediately
    updateIntercomVisibility();

    // Watch for Intercom to be added to the DOM
    const observer = new MutationObserver(() => {
      if (updateIntercomVisibility()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
      // Show Intercom again when component unmounts
      const intercomElement = document.querySelector(
        '.intercom-lightweight-app'
      ) as HTMLElement;

      if (intercomElement) {
        intercomElement.style.display = '';
      }
    };
  }, [isMobile]);

  // Sync step to backend whenever URL step changes (user navigation)
  const previousUrlStepRef = useRef<string | null>(null);
  const previousRfqIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Update backend if:
    // 1. We have both rfq_id and urlStep
    // 2. Either: rfqId just changed (new RFQ created) OR urlStep changed (user navigated)
    const rfqIdChanged =
      previousRfqIdRef.current !== rfqId && previousRfqIdRef.current !== null;
    const stepChanged =
      previousUrlStepRef.current !== urlStep &&
      previousUrlStepRef.current !== null;
    const newRfqCreated =
      previousRfqIdRef.current === null && rfqId !== null && urlStep !== null;

    if (rfqId && urlStep && (rfqIdChanged || stepChanged || newRfqCreated)) {
      updateStep({
        rfq_id: parseInt(rfqId, 10),
        builder_step: urlStep as BuilderStep
      });
    }

    previousUrlStepRef.current = urlStep;
    previousRfqIdRef.current = rfqId;
  }, [urlStep, rfqId, updateStep]);

  const handleNext = () => {
    setDirection(1);
  };

  const renderPage = () => {
    switch (step) {
      case 'landing':
        return (
          <RfqBuilderLanding
            generateRfq={generateRfq}
            handleNext={handleNext}
          />
        );
      case 'intro':
        return <RfqBuilderIntro rfq={data} handleNext={handleNext} />;
      case 'path':
        return <RfqPathSelection rfq={data} handleNext={handleNext} />;
      case 'playground':
      case 'review':
        return (
          <RfqPlayground
            rfq={data}
            generateRfq={generateRfq}
            generatedContent={generatedContent}
            isGenerating={isGenerating}
            initialDrawerOpen={step === 'review'}
          />
        );
      default:
        return (
          <RfqBuilderLanding
            generateRfq={generateRfq}
            handleNext={handleNext}
          />
        );
    }
  };

  const variants = isMobile ? mobileVariants : desktopVariants;
  const transition = isMobile ? mobileTransition : desktopTransition;

  // Show spinner while loading an existing RFQ (not when creating a new one)
  // Only show spinner if we started with an rfqId in the URL
  if (rfqId && !data?.rfq && initialRfqIdRef.current) {
    return (
      <div className="flex justify-center items-center h-dvh">
        <Spinner />
      </div>
    );
  }

  return (
    <RfqConversationProvider rfqId={data?.rfq?.id}>
      <div
        data-testid="build-anything-display"
        className="h-dvh flex flex-col bg-white relative overflow-hidden"
      >
        <RfqBuilderHeader />
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step === 'review' ? 'playground' : step}
            custom={direction}
            variants={variants}
            initial={direction === 0 ? false : 'enter'}
            animate="center"
            exit="exit"
            transition={transition}
            className="absolute inset-0"
          >
            {renderPage()}
          </motion.div>
        </AnimatePresence>
      </div>
    </RfqConversationProvider>
  );
};

export default BuildAnythingPage;
